import React from 'react';
import { z } from 'zod';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { InputField } from 'components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { Button, Stack, Typography, Box } from '@mui/material';

import { usePartner } from '../../api';
import { newPartnerFormSchema } from '../../model';
import { partnerMapper } from '../../mapper/PartnerMapper';

export const CreatePartnerAccountDialog = () => {
  const { isOpen, handleClose } = useModal(ModalsKeys.AftermarketCreatePartnerDialog);

  const { handleCreatePartnerAccount, isPartnerAccountCreating } = usePartner();

  const { control, handleSubmit: onSubmit } = useForm<z.infer<typeof newPartnerFormSchema>>({
    resolver: zodResolver(newPartnerFormSchema),
    defaultValues: {
      contact: { title: '', email: '', phone: '', lastName: '', firstName: '' },
    },
  });

  const handleSubmit = async (formData: z.infer<typeof newPartnerFormSchema>) => {
    const createDTO = partnerMapper.processCreateAccountFormToAPIDTO(formData);
    await handleCreatePartnerAccount(createDTO);
    handleClose();
  };

  return (
    <CustomDialog
      maxWidth="lg"
      isOpen={isOpen}
      label="Add New Vendor"
      onClose={handleClose}
      actions={
        <>
          <Button onClick={handleClose} disabled={isPartnerAccountCreating}>
            Cancel
          </Button>

          <Button variant="contained" onClick={onSubmit(handleSubmit)} loading={isPartnerAccountCreating}>
            Add Partner
          </Button>
        </>
      }
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Typography variant="subtitle1" component="span">
            Vendor
          </Typography>

          <InputField
            control={control}
            name="contact.title"
            label="Vendor Name"
            inputProps={{ variant: 'outlined', required: true }}
          />
        </Stack>

        <Stack gap="0.5rem">
          <Typography variant="subtitle1" component="span">
            Contact
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <InputField
              control={control}
              label="First Name"
              name="contact.firstName"
              inputProps={{ variant: 'outlined', required: true }}
            />

            <InputField
              control={control}
              label="Last Name"
              name="contact.lastName"
              inputProps={{ variant: 'outlined', required: true }}
            />

            <InputField
              label="Email"
              control={control}
              name="contact.email"
              inputProps={{ variant: 'outlined', type: 'email', required: true }}
            />

            <InputField
              label="Phone"
              control={control}
              name="contact.phone"
              inputProps={{ variant: 'outlined', type: 'tel', mask: '(999) 999-9999' }}
            />
          </Box>
        </Stack>
      </Stack>
    </CustomDialog>
  );
};
